<template>
  <div class="concurrent-force-pro">
    
    <div class="viz-header">
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula" v-latex>$ΣF = 0$</div>
    </div>

    <!-- 主布局：上下结构以适配 50% 宽度 -->
    <div class="main-layout vertical">
      <!-- 渲染区 -->
      <div class="canvas-3d-wrapper interactive" @mousemove="onMouseMove" @mouseup="onMouseUp">
        <svg width="100%" height="100%" viewBox="0 0 400 400" preserveAspectRatio="xMidYMid meet">
          <defs>
            <pattern id="grid-pro" width="20" height="20" patternUnits="userSpaceOnUse">
              <path d="M 20 0 L 0 0 0 20" fill="none" stroke="rgba(0, 210, 255, 0.05)" stroke-width="0.5"/>
            </pattern>
          </defs>
          <rect width="400" height="400" fill="url(#grid-pro)" />

          <!-- 坐标辅助 -->
          <line x1="200" y1="50" x2="200" y2="350" stroke="rgba(255,255,255,0.03)" />
          <line x1="50" y1="200" x2="350" y2="200" stroke="rgba(255,255,255,0.03)" />

          <!-- 矢量计算展示层 -->
          <g transform="translate(200, 200)">
            <!-- F3 (自动平衡) -->
            <line x1="0" y1="0" :x2="f3.x" :y2="f3.y" stroke="#1e90ff" stroke-width="4" marker-end="url(#arr-3)" />
            <text :x="f3.x * 0.6" :y="f3.y * 0.6 - 10" fill="#1e90ff" font-size="11" font-weight="bold" text-anchor="middle">F3</text>

            <!-- F1 -->
            <line x1="0" y1="0" :x2="f1.x" :y2="f1.y" stroke="#ff4757" stroke-width="4" marker-end="url(#arr-1)" />
            <text :x="f1.x * 1.1" :y="f1.y * 1.1 + 5" fill="#ff4757" font-size="12" font-weight="bold" text-anchor="middle">F1</text>
            
            <!-- F2 -->
            <line x1="0" y1="0" :x2="f2.x" :y2="f2.y" stroke="#2ed573" stroke-width="4" marker-end="url(#arr-2)" />
            <text :x="f2.x * 1.1" :y="f2.y * 1.1 + 5" fill="#2ed573" font-size="12" font-weight="bold" text-anchor="middle">F2</text>

            <!-- 核心点 -->
            <circle cx="0" cy="0" r="5" fill="#fff" />
          </g>

          <!-- 交互手柄 -->
          <circle :cx="200 + f1.x" :cy="200 + f1.y" r="15" fill="transparent" style="cursor: move" @mousedown="onMouseDown('f1')" />
          <circle :cx="200 + f2.x" :cy="200 + f2.y" r="15" fill="transparent" style="cursor: move" @mousedown="onMouseDown('f2')" />

          <defs>
            <marker id="arr-1" markerWidth="8" markerHeight="6" refX="8" refY="3" orient="auto"> <polygon points="0 0, 8 3, 0 6" fill="#ff4757" /> </marker>
            <marker id="arr-2" markerWidth="8" markerHeight="6" refX="8" refY="3" orient="auto"> <polygon points="0 0, 8 3, 0 6" fill="#2ed573" /> </marker>
            <marker id="arr-3" markerWidth="8" markerHeight="6" refX="8" refY="3" orient="auto"> <polygon points="0 0, 8 3, 0 6" fill="#1e90ff" /> </marker>
          </defs>
        </svg>

        <!-- 矢量三角形小窗 -->
        <div class="triangle-preview">
           <svg width="100" height="100" viewBox="-50 -50 100 100">
             <line x1="0" y1="0" :x2="f1.x/3" :y2="f1.y/3" stroke="#ff4757" stroke-width="1" />
             <line :x1="f1.x/3" :y1="f1.y/3" :x2="(f1.x+f2.x)/3" :y2="(f1.y+f2.y)/3" stroke="#2ed573" stroke-width="1" />
             <line :x1="(f1.x+f2.x)/3" :y1="(f1.y+f2.y)/3" x2="0" y2="0" stroke="#1e90ff" stroke-width="1" />
           </svg>
           <div class="label">封闭三角形</div>
        </div>
      </div>

      <!-- 参数控制区 -->
      <div class="controls-container">
        <div class="param-card">
          <div class="row">
            <div class="col">
              <label>F1 大小: {{ mag1 }}N</label>
              <el-slider v-model="sliderMag1" :min="10" :max="120" size="mini" @input="updateFromSliders"></el-slider>
            </div>
            <div class="col">
              <label>F1 角度: {{ ang1 }}°</label>
              <el-slider v-model="sliderAng1" :min="0" :max="360" size="mini" @input="updateFromSliders"></el-slider>
            </div>
          </div>
          <div class="row">
            <div class="col">
              <label>F2 大小: {{ mag2 }}N</label>
              <el-slider v-model="sliderMag2" :min="10" :max="120" size="mini" @input="updateFromSliders"></el-slider>
            </div>
            <div class="col">
              <label>F2 角度: {{ ang2 }}°</label>
              <el-slider v-model="sliderAng2" :min="0" :max="360" size="mini" @input="updateFromSliders"></el-slider>
            </div>
          </div>
        </div>

        <div class="hint-card">
          <i class="el-icon-magic-stick"></i>
          <span><b>操作提示：</b>可直接在画布中拖拽力箭头，参数面板会同步更新。</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ConcurrentForceBalanceViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      f1: { x: 80, y: -60 },
      f2: { x: -70, y: -50 },
      sliderMag1: 100,
      sliderAng1: 323,
      sliderMag2: 86,
      sliderAng2: 215,
      dragging: null
    }
  },
  computed: {
    f3() { return { x: -(this.f1.x + this.f2.x), y: -(this.f1.y + this.f2.y) }; },
    mag1() { return Math.sqrt(this.f1.x**2 + this.f1.y**2).toFixed(0); },
    mag2() { return Math.sqrt(this.f2.x**2 + this.f2.y**2).toFixed(0); },
    ang1() { return (Math.atan2(this.f1.y, this.f1.x) * 180 / Math.PI + 360) % 360 | 0; },
    ang2() { return (Math.atan2(this.f2.y, this.f2.x) * 180 / Math.PI + 360) % 360 | 0; }
  },
  mounted() {
    this.syncSlidersFromState();
  },
  methods: {
    syncSlidersFromState() {
      this.sliderMag1 = parseInt(this.mag1);
      this.sliderAng1 = this.ang1;
      this.sliderMag2 = parseInt(this.mag2);
      this.sliderAng2 = this.ang2;
    },
    updateFromSliders() {
      const r1 = this.sliderAng1 * Math.PI / 180;
      this.f1 = { x: this.sliderMag1 * Math.cos(r1), y: this.sliderMag1 * Math.sin(r1) };
      const r2 = this.sliderAng2 * Math.PI / 180;
      this.f2 = { x: this.sliderMag2 * Math.cos(r2), y: this.sliderMag2 * Math.sin(r2) };
    },
    onMouseDown(target) { this.dragging = target; },
    onMouseMove(e) {
      if (!this.dragging) return;
      const rect = e.currentTarget.getBoundingClientRect();
      const x = e.clientX - rect.left - rect.width/2;
      const y = e.clientY - rect.top - rect.height/2;
      // 保持一定比例缩放适配 ViewBox
      const scale = 400 / rect.width;
      if (this.dragging === 'f1') this.f1 = { x: x * scale, y: y * scale };
      else if (this.dragging === 'f2') this.f2 = { x: x * scale, y: y * scale };
      this.syncSlidersFromState();
    },
    onMouseUp() { this.dragging = null; }
  }
}
</script>

<style scoped lang="scss">
.concurrent-force-pro {
  flex: 1; display: flex; flex-direction: column; gap: 12px; height: 100%; overflow: hidden;
}

.viz-header {
  display: flex; justify-content: space-between; align-items: center; padding-bottom: 5px;
  .title { font-size: 1.1rem; color: #fff; font-weight: 300; }
  .formula-chip { padding: 4px 12px; background: rgba(0, 210, 255, 0.1); border-radius: 8px; color: #00d2ff; font-family: serif; font-style: italic; font-weight: bold; }
}

.main-layout {
  flex: 1; display: flex; flex-direction: column; gap: 15px; min-height: 0;
}

.canvas-3d-wrapper {
  flex: 1.2; position: relative; background: rgba(0,0,0,0.5); border-radius: 12px; border: 1px solid rgba(255,255,255,0.05); overflow: hidden;
  
  .triangle-preview {
    position: absolute; top: 15px; right: 15px; width: 80px; height: 100px; background: rgba(0,0,0,0.4); border: 1px solid rgba(255,255,255,0.1); border-radius: 8px; display: flex; flex-direction: column; align-items: center; padding: 5px;
    svg { flex: 1; }
    .label { font-size: 0.55rem; color: #888; text-transform: uppercase; margin-top: 2px; }
  }
}

.controls-container {
  flex: 0.8; display: flex; flex-direction: column; gap: 12px; overflow-y: auto; padding-bottom: 10px;
}

.param-card {
  background: rgba(255, 255, 255, 0.03); border: 1px solid rgba(255, 255, 255, 0.05); border-radius: 12px; padding: 15px;
  .row { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 12px; &:last-child { margin-bottom: 0; } }
  .col { label { display: block; font-size: 0.7rem; color: #aaa; margin-bottom: 6px; } }
}

.hint-card {
  background: rgba(0, 210, 255, 0.05); border-radius: 8px; padding: 10px 15px; color: #00d2ff; font-size: 0.75rem; display: flex; align-items: center; gap: 10px;
  i { font-size: 1rem; }
  b { color: #fff; }
}

/* 文字位置微调防止遮挡 */
text { text-shadow: 0 0 4px rgba(0,0,0,0.8); pointer-events: none; }
</style>
